import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Tooltip } from '@/components/shared/Tooltip';
import { useSetupStatus } from '@/hooks/useSetupStatus';
import { useSystemHealth, type HealthState } from '@/hooks/useSystemHealth';
import { usePublishQueue } from '@/contexts/PublishQueueContext';
import { type NavItem, PLATFORM_NAV, PLATFORM_HELP } from '@/components/layout/navItems';
import { IconCheck, IconChecklist } from '@/lib/icons';

// GlobalRail is the slim, icon-only far-left rail for platform-scoped nav. It
// is ALWAYS visible and ALWAYS narrow (no expand state — that belongs to the
// workspace column to its right). Items: an "AEO" brand mark, then Sites,
// Alerts, Prompt Templates, Setup, Settings, Help. Setup is injected before
// Settings with a status dot; Sites/Settings carry health dots and Alerts a
// count badge, reusing the same state the old sidebar consumed.

const SETUP_ITEM: NavItem = {
  to: '/setup',
  label: 'Setup',
  icon: IconChecklist,
  end: true,
};

function dotClass(state: HealthState): string {
  switch (state) {
    case 'warning':
      return 'bg-warning';
    case 'error':
      return 'bg-error';
    default:
      return '';
  }
}

export function GlobalRail() {
  const health = useSystemHealth();
  const queue = usePublishQueue();
  const setup = useSetupStatus();
  const setupIncomplete = !setup.loading && !setup.allRequiredComplete;
  const setupComplete = !setup.loading && setup.allRequiredComplete;

  return (
    <aside
      aria-label="Platform"
      className="w-14 shrink-0 border-r border-dash-border bg-dash-surface/60 h-full flex flex-col"
    >
      <div className="px-2 py-4 border-b border-dash-border flex items-center justify-center">
        <span
          className="text-xs font-mono font-semibold text-dash-primary"
          aria-label="AEO Platform"
        >
          AEO
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {PLATFORM_NAV.map((item) => {
          // Setup sits immediately before Settings in the target order.
          const settingsBoundary = item.to === '/settings';
          const healthState =
            item.healthDot === 'sites'
              ? health.sitesStatus
              : item.healthDot === 'settings'
                ? health.settingsStatus
                : null;
          return (
            <Fragment key={item.to}>
              {settingsBoundary ? (
                <RailItem
                  item={SETUP_ITEM}
                  warningDot={setupIncomplete}
                  checkIcon={setupComplete}
                />
              ) : null}
              <RailItem
                item={item}
                healthState={healthState}
                alertCount={item.alertCounter === true ? health.alertCount : 0}
                queueCount={item.queueCounter === true ? queue.counts.in_flight : 0}
              />
            </Fragment>
          );
        })}
        <RailItem item={PLATFORM_HELP} />
      </nav>
    </aside>
  );
}

function RailItem({
  item,
  healthState,
  alertCount = 0,
  queueCount = 0,
  warningDot = false,
  checkIcon = false,
}: {
  item: NavItem;
  healthState?: HealthState | null;
  alertCount?: number;
  queueCount?: number;
  warningDot?: boolean;
  checkIcon?: boolean;
}) {
  const Icon = item.icon;
  const dotState = healthState ?? null;
  const showHealthDot = dotState !== null && dotState !== 'ok';
  const showCount = alertCount > 0 && !showHealthDot;
  const countLabel = alertCount >= 100 ? '99+' : String(alertCount);
  const showQueue = queueCount > 0 && !showHealthDot && !showCount;
  const queueLabel = queueCount >= 100 ? '99+' : String(queueCount);

  return (
    <Tooltip placement="right" content={item.label}>
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          `relative flex items-center justify-center px-2 py-2 rounded-md text-sm transition-colors border-l-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-1 focus-visible:ring-offset-dash-bg ${
            isActive
              ? 'bg-accent/10 text-accent'
              : 'text-dash-secondary hover:bg-dash-surface hover:text-dash-primary border-transparent'
          }`
        }
        style={({ isActive }) =>
          isActive
            ? { borderLeftColor: 'var(--site-accent, var(--color-accent, #22d3ee))' }
            : undefined
        }
      >
        <span className="relative shrink-0 inline-flex items-center justify-center">
          <Icon size={18} />
          {showHealthDot ? (
            <span
              aria-label={`${item.healthDot} status: ${dotState}`}
              className={`absolute -top-0.5 -right-0.5 inline-block w-2 h-2 rounded-full ${dotClass(dotState!)}`}
            />
          ) : null}
          {showCount ? (
            <span
              aria-label={`${countLabel} unread error alerts`}
              className="absolute -top-1 -right-2 text-[9px] font-mono leading-none text-error bg-error/[0.15] border border-error/40 rounded px-1 py-px"
            >
              {countLabel}
            </span>
          ) : null}
          {showQueue ? (
            <span
              aria-label={`${queueLabel} publishing`}
              className="absolute -top-1 -right-2 text-[9px] font-mono leading-none text-accent bg-accent/[0.15] border border-accent/40 rounded px-1 py-px"
            >
              {queueLabel}
            </span>
          ) : null}
          {warningDot ? (
            <span
              aria-label="Setup incomplete"
              className="absolute -top-0.5 -right-0.5 inline-block w-2 h-2 rounded-full bg-warning"
            />
          ) : null}
          {checkIcon ? (
            <span
              aria-hidden="true"
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success text-dash-bg flex items-center justify-center"
            >
              <IconCheck size={8} />
            </span>
          ) : null}
        </span>
        <span className="sr-only">{item.label}</span>
      </NavLink>
    </Tooltip>
  );
}
